import { useCallback, useEffect, useState } from "react";
import { ChevronDown, Copy, Trophy } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { formatBRL, formatDateRangeUTC, formatDateTime } from "@/lib/format";
import { Card } from "@/components/ui/Card";
import { Chip } from "@/components/ui/Chip";
import { ScreenSpinner } from "@/components/ui/Spinner";
import { useToast } from "@/components/ui/Toast";
import { AdminHeader, errMessage } from "./shared";

type WinnerPayout = {
  userId: string;
  userName: string;
  stake: number;
  prize: number;
  betId: string;
  playerIds: string[];
  playerNames: string[];
  email: string;
  pixKey: string | null;
  savedPixKeys: string[];
};

type TournamentResult = {
  tournamentId: string;
  tournamentName: string;
  tournamentStartDate: string;
  tournamentEndDate: string;
  settledAt: string;
  totalStaked: number;
  totalPot: number;
  platformFee: number;
  winnersPool: number;
  interest: number;
  platformProfit: number;
  winnersCount: number;
  prizePerWinner: number;
  jackpotAdded: number;
  jackpotIn: number;
  platformContribution: number;
  minPot: number | null;
  winnerPayouts: WinnerPayout[];
};

type ResultsResponse = { results: TournamentResult[] };

// Verifica um palpite: mostra os 3 nomes escolhidos na aposta vencedora
// (fallback pro id quando o snapshot do nome não existe — apostas antigas).
function pickLabels(w: WinnerPayout): string[] {
  return w.playerIds.map((id, i) => w.playerNames[i] || id);
}

export default function AdminResultadosPage() {
  const { showToast } = useToast();
  const [results, setResults] = useState<TournamentResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedBetId, setExpandedBetId] = useState<string | null>(null);

  const copyPixKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      showToast({ type: "success", message: "Chave PIX copiada." });
    } catch {
      showToast({ type: "error", message: "Não foi possível copiar." });
    }
  };

  const load = useCallback(async () => {
    try {
      const data = await apiFetch<ResultsResponse>(
        "/api/admin/tournament-results",
      );
      setResults(data.results);
      setError(null);
    } catch (err) {
      setError(errMessage(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="flex flex-col gap-4 px-5 pt-6">
      <AdminHeader
        title="Resultados"
        caption={
          results
            ? `${results.length} torneio${results.length === 1 ? "" : "s"} liquidado${results.length === 1 ? "" : "s"}`
            : undefined
        }
      />

      {error && (
        <Card className="p-4 text-center text-sm text-[#ffb4ad]">{error}</Card>
      )}
      {!results && !error && <ScreenSpinner />}

      <div className="flex flex-col gap-4 pb-6">
        {results?.map((r) => (
          <Card key={r.tournamentId} className="flex flex-col gap-3 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-bold">{r.tournamentName}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {formatDateRangeUTC(r.tournamentStartDate, r.tournamentEndDate)}
                </p>
              </div>
              <Chip tone="muted">Liquidado {formatDateTime(r.settledAt)}</Chip>
            </div>

            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span>
                Apostado{" "}
                <span className="font-semibold text-foreground">
                  {formatBRL(r.totalStaked)}
                </span>
              </span>
              <span>
                Pote total{" "}
                <span className="font-semibold text-foreground">
                  {formatBRL(r.totalPot)}
                </span>
              </span>
              <span>
                Pote vencedores{" "}
                <span className="font-semibold text-foreground">
                  {formatBRL(r.winnersPool)}
                </span>
              </span>
              {r.jackpotAdded > 0 && (
                <span>
                  Jackpot carregado{" "}
                  <span className="font-semibold text-foreground">
                    {formatBRL(r.jackpotAdded)}
                  </span>
                </span>
              )}
              {r.jackpotIn > 0 && (
                <span>
                  Jackpot recebido{" "}
                  <span className="font-semibold text-foreground">
                    {formatBRL(r.jackpotIn)}
                  </span>
                </span>
              )}
              <span>
                Lucro plataforma{" "}
                <span className="font-semibold text-foreground">
                  {formatBRL(r.platformProfit)}
                </span>
              </span>
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-[10px] font-semibold tracking-[1.2px] text-muted-foreground">
                {r.winnersCount === 0
                  ? "SEM GANHADORES"
                  : `${r.winnersCount} GANHADOR${r.winnersCount === 1 ? "" : "ES"} · ${formatBRL(r.prizePerWinner)} CADA`}
              </p>

              {r.winnerPayouts.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Ninguém acertou os 3 do Top 3 neste torneio.
                </p>
              )}

              {r.winnerPayouts.map((w) => {
                const expanded = expandedBetId === w.betId;
                const otherKeys = w.savedPixKeys.filter(
                  (k) => k !== w.pixKey,
                );
                return (
                  <div
                    key={w.betId}
                    className="rounded-(--radius-field) bg-secondary"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedBetId(expanded ? null : w.betId)
                      }
                      className="flex w-full items-center gap-3 p-3 text-left"
                      aria-expanded={expanded}
                    >
                      {/* Ouro (--color-accent) destaca visualmente um pagamento
                          real de prêmio — cor do design system, não hardcoded. */}
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent/20">
                        <Trophy size={14} className="text-accent" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold">
                          {w.userName}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {pickLabels(w).join(" · ")}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-0.5">
                        <p className="text-sm font-bold text-tint">
                          +{formatBRL(w.prize)}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          apostou {formatBRL(w.stake)}
                        </p>
                      </div>
                      <ChevronDown
                        size={16}
                        className={`shrink-0 text-muted-foreground transition-transform ${
                          expanded ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {expanded && (
                      <div className="flex flex-col gap-2 border-t border-border/60 px-3 pb-3 pt-2 text-xs">
                        <div className="flex justify-between gap-2">
                          <span className="text-muted-foreground">
                            Apostado
                          </span>
                          <span className="font-semibold">
                            {formatBRL(w.stake)}
                          </span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-muted-foreground">
                            Prêmio a pagar
                          </span>
                          <span className="font-bold text-tint">
                            {formatBRL(w.prize)}
                          </span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-muted-foreground">
                            E-mail
                          </span>
                          <span className="truncate font-semibold">
                            {w.email}
                          </span>
                        </div>

                        <div className="mt-1 flex flex-col gap-1">
                          <span className="text-[10px] font-semibold tracking-[1.2px] text-muted-foreground">
                            CHAVE PIX ATIVA
                          </span>
                          {w.pixKey ? (
                            <div className="flex items-center justify-between gap-2 rounded-(--radius-field) bg-card px-3 py-2">
                              <span className="truncate font-semibold">
                                {w.pixKey}
                              </span>
                              <button
                                type="button"
                                title="Copiar chave PIX"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void copyPixKey(w.pixKey!);
                                }}
                                className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
                              >
                                <Copy size={14} />
                              </button>
                            </div>
                          ) : (
                            <p className="text-[#ffb4ad]">
                              Nenhuma chave PIX cadastrada — contatar o
                              jogador antes de pagar.
                            </p>
                          )}
                        </div>

                        {otherKeys.length > 0 && (
                          <div className="mt-1 flex flex-col gap-1">
                            <span className="text-[10px] font-semibold tracking-[1.2px] text-muted-foreground">
                              OUTRAS CHAVES SALVAS
                            </span>
                            {otherKeys.map((k) => (
                              <div
                                key={k}
                                className="flex items-center justify-between gap-2 rounded-(--radius-field) bg-card px-3 py-2"
                              >
                                <span className="truncate">{k}</span>
                                <button
                                  type="button"
                                  title="Copiar chave PIX"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void copyPixKey(k);
                                  }}
                                  className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
                                >
                                  <Copy size={14} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        ))}

        {results && results.length === 0 && (
          <Card className="p-6 text-center text-sm text-muted-foreground">
            Nenhum torneio liquidado ainda.
          </Card>
        )}
      </div>
    </div>
  );
}
